import React, { useState, useEffect, useCallback, useRef, useContext } from 'react';
import {
  Box,
  Typography,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  InputAdornment,
  Select,
  MenuItem,
  Switch,
  Chip,
  Divider,
  Tooltip,
  IconButton,
  Input,
  Badge,
  Alert,
  useTheme,
  Button
} from '@mui/material';
import {
  Add as AddIcon,
  Help as HelpIcon,
  Code as CodeIcon,
  CheckCircle as CheckCircleIcon,
  Label as LabelIcon,
  Key as KeyIcon,
  Home as AddressIcon,
  VisibilityOff,
  CloudUpload,
  LocationOn as LocationIcon,
  Folder as FolderIcon,
  Storage as StorageIcon
} from '@mui/icons-material';
import { generateRandomString } from '../../../../utils/stringUtils';
import BaseModal from '../../../../components/BaseModal';
import { PhoneTextField } from '../PhoneNumberMask';

const FieldTypes = [
  { value: 'text', label: 'Texto', icon: '🔤' },
  { value: 'email', label: 'E-mail', icon: '📧' },
  { value: 'phone', label: 'Telefone', icon: '📱' },
  { value: 'select', label: 'Seleção', icon: '📋' },
  { value: 'checkbox', label: 'Caixa de Seleção', icon: '✓' },
  { value: 'radio', label: 'Opções Múltiplas', icon: '⚪' },
  { value: 'date', label: 'Data', icon: '📅' },
  { value: 'address', label: 'Endereço', icon: '🏠' },
  { value: 'hidden', label: 'Campo Oculto', icon: '🔒' },
  { value: 'file', label: 'Upload de Arquivo', icon: '📎' }
];

const ValidationRules = {
  email: /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/,
  phone: /^\+?[0-9\s\-()]{8,20}$/,
  text: /.+/,
  date: /.+/
};

const FieldEditorDialog = ({ open, onClose, onSave, field }) => {
  const [fieldData, setFieldData] = useState({
    id: '',
    name: '', // Novo campo para nome personalizado
    type: 'text',
    label: '',
    placeholder: '',
    required: true,
    options: [],
    validation: '',
    order: 0,
    // Configurações específicas para novos tipos de campo
    addressConfig: {
      enableAutocomplete: true,
      enableGeolocation: false,
      fields: {
        zipCode: true,
        street: true,
        number: true,
        complement: false,
        neighborhood: true,
        city: true,
        state: true,
        country: false
      }
    },
    hiddenConfig: {
      value: '',
      paramName: '',
      allowOverride: true
    },
    fileConfig: {
      maxSize: 5 * 1024 * 1024, // 5MB
      allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
      multiple: false,
      storage: 'local',
      folder: 'uploads'
    }
  });
  
  const [newOption, setNewOption] = useState('');
  const [errors, setErrors] = useState({});
  const theme = useTheme();
  
  // Inicializar com dados do campo, se for edição
  useEffect(() => {
    if (field) {
      setFieldData({
        ...field,
        options: field.options || [],
        name: field.name || field.id, // Use o name se existir, ou o id como fallback
        // Garantir que as configurações dos novos tipos existam
        addressConfig: field.addressConfig || {
          enableAutocomplete: true,
          enableGeolocation: false,
          fields: {
            zipCode: true,
            street: true,
            number: true,
            complement: false,
            neighborhood: true,
            city: true,
            state: true,
            country: false
          }
        },
        hiddenConfig: field.hiddenConfig || {
          value: '',
          paramName: '',
          allowOverride: true
        },
        fileConfig: field.fileConfig || {
          maxSize: 5 * 1024 * 1024, // 5MB
          allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
          multiple: false,
          storage: 'local',
          folder: 'uploads',
          enablePdfParsing: false,
          pdfParsingConfig: {
            extractText: true,
            extractMetadata: true,
            extractFields: true,
            saveAsCustomFields: true,
            fieldPrefix: 'pdf_',
            structuredData: {
              enabled: false,
              patterns: []
            }
          }
        }
      });
    } else {
      // Reset para valores padrão se for um novo campo
      const newId = generateRandomString(8);
      setFieldData({
        id: `field_${newId}`,
        name: `field_${newId}`,
        type: 'text',
        label: '',
        placeholder: '',
        required: true,
        options: [],
        validation: '',
        order: 0,
        addressConfig: {
          enableAutocomplete: true,
          enableGeolocation: false,
          fields: {
            zipCode: true,
            street: true,
            number: true,
            complement: false,
            neighborhood: true,
            city: true,
            state: true,
            country: false
          }
        },
        hiddenConfig: {
          value: '',
          paramName: '',
          allowOverride: true
        },
        fileConfig: {
          maxSize: 5 * 1024 * 1024, // 5MB
          allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
          multiple: false,
          storage: 'local',
          folder: 'uploads',
          enablePdfParsing: false,
          pdfParsingConfig: {
            extractText: true,
            extractMetadata: true,
            extractFields: true,
            saveAsCustomFields: true,
            fieldPrefix: 'pdf_',
            structuredData: {
              enabled: false,
              patterns: []
            }
          }
        }
      });
    }
    
    setErrors({});
    setNewOption('');
  }, [field, open]);
  
  // Handler para mudanças em campos de texto
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFieldData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo alterado
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };
  
  // Atualizar automaticamente o nome do campo quando o rótulo for alterado
  useEffect(() => {
    // Só atualiza o nome se for um campo novo e o usuário não tiver definido manualmente
    if (!field && fieldData.label && !fieldData.customNameSet) {
      // Converte o rótulo para um formato de nome de campo (snake_case)
      const generatedName = fieldData.label
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
      
      if (generatedName) {
        setFieldData(prev => ({
          ...prev,
          name: generatedName
        }));
      }
    }
  }, [fieldData.label, field, fieldData.customNameSet]);
  
  // Handler para quando o usuário edita manualmente o nome do campo
  const handleNameChange = (e) => {
    const value = e.target.value;
    
    // Formatar como identificador válido (somente letras, números e underscore)
    const formattedValue = value.replace(/[^a-z0-9_]/gi, '');
    
    setFieldData(prev => ({
      ...prev,
      name: formattedValue,
      customNameSet: true // Marca que o usuário definiu manualmente
    }));
    
    // Limpar erro
    if (errors.name) {
      setErrors(prev => ({
        ...prev,
        name: null
      }));
    }
  };
  
  // Handler para mudança na opção "required"
  const handleRequiredChange = (e) => {
    setFieldData(prev => ({
      ...prev,
      required: e.target.checked
    }));
  };
  
  // Handler para adicionar opção (para campos select, checkbox, radio)
  const handleAddOption = () => {
    if (!newOption.trim()) return;
    
    setFieldData(prev => ({
      ...prev,
      options: [...prev.options, newOption.trim()]
    }));
    
    setNewOption('');
    
    // Limpar erro de opções se existir
    if (errors.options) {
      setErrors(prev => ({
        ...prev,
        options: null
      }));
    }
  };
  
  // Handler para remover opção
  const handleRemoveOption = (index) => {
    setFieldData(prev => ({
      ...prev,
      options: prev.options.filter((_, i) => i !== index)
    }));
  };
  
  // Handler para atualizar configuração de endereço
  const handleAddressConfigChange = (key, value) => {
    setFieldData(prev => ({
      ...prev,
      addressConfig: {
        ...prev.addressConfig,
        [key]: value
      }
    }));
  };
  
  // Handler para atualizar campos de endereço
  const handleAddressFieldChange = (field, checked) => {
    setFieldData(prev => ({
      ...prev,
      addressConfig: {
        ...prev.addressConfig,
        fields: {
          ...prev.addressConfig.fields,
          [field]: checked
        }
      }
    }));
  };
  
  // Handler para atualizar configuração de campo oculto
  const handleHiddenConfigChange = (key, value) => {
    setFieldData(prev => ({
      ...prev,
      hiddenConfig: {
        ...prev.hiddenConfig,
        [key]: value
      }
    }));
  };
  
  // Handler para atualizar configuração de upload de arquivo
  const handleFileConfigChange = (key, value) => {
    setFieldData(prev => ({
      ...prev,
      fileConfig: {
        ...prev.fileConfig,
        [key]: value
      }
    }));
  };
  
  // Handler para atualizar tipos de arquivo permitidos
  const handleAllowedTypesChange = (types) => {
    setFieldData(prev => ({
      ...prev,
      fileConfig: {
        ...prev.fileConfig,
        allowedTypes: types
      }
    }));
  };
  
  // Validar formulário antes de salvar
  const validateForm = () => {
    const newErrors = {};
    
    if (!fieldData.label.trim()) {
      newErrors.label = 'O rótulo do campo é obrigatório';
    }
    
    if (!fieldData.name.trim()) {
      newErrors.name = 'O nome do campo é obrigatório';
    }
    
    if (['select', 'checkbox', 'radio'].includes(fieldData.type) && fieldData.options.length === 0) {
      newErrors.options = 'Adicione pelo menos uma opção';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // Handler para salvar o campo
  const handleSave = () => {
    if (validateForm()) {
      // Atualizar o id do campo para ficar igual ao nome (para compatibilidade)
      const updatedFieldData = {
        ...fieldData,
        id: fieldData.name // Usar o name como id
      };
      onSave(updatedFieldData);
    }
  };
  
  // Handler para tecla Enter no campo de nova opção
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddOption();
    }
  };

  // Ações para o Modal
  const modalActions = [
    {
      label: "Cancelar",
      onClick: onClose,
      variant: "outlined",
      color: "inherit"
    },
    {
      label: "Salvar",
      onClick: handleSave,
      variant: "contained",
      color: "primary",
      icon: <CheckCircleIcon />
    }
  ];
  
  // Renderizar campo adequado baseado no tipo selecionado
  const renderFieldPreview = () => {
    switch (fieldData.type) {
      case 'text':
        return (
          <TextField
            fullWidth
            label={fieldData.label || "Rótulo do Campo"}
            placeholder={fieldData.placeholder}
            disabled
            required={fieldData.required}
            margin="normal"
            variant="outlined"
            InputProps={{
              style: {
                borderColor: theme.palette.primary.main
              }
            }}
          />
        );
      case 'email':
        return (
          <TextField
            fullWidth
            type="email"
            label={fieldData.label || "E-mail"}
            placeholder={fieldData.placeholder}
            disabled
            required={fieldData.required}
            margin="normal"
            variant="outlined"
            InputProps={{
              style: {
                borderColor: theme.palette.primary.main
              }
            }}
          />
        );
      case 'phone':
        return (
          <PhoneTextField
            label={fieldData.label || "Telefone"}
            placeholder={fieldData.placeholder}
            value=""
            onChange={() => {}}
            name="phone-preview"
            required={fieldData.required}
            margin="normal"
            disabled
            InputProps={{
              style: {
                borderColor: theme.palette.primary.main
              }
            }}
          />
        );
      case 'select':
        return (
          <FormControl fullWidth margin="normal" disabled>
            <InputLabel>{fieldData.label || "Seleção"}</InputLabel>
            <Select
              label={fieldData.label || "Seleção"}
              placeholder={fieldData.placeholder}
              disabled
              required={fieldData.required}
              InputProps={{
                style: {
                  borderColor: theme.palette.primary.main
                }
              }}
            >
              {fieldData.options.map((option, index) => (
                <MenuItem key={index} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      case 'checkbox':
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {fieldData.label || "Caixa de Seleção"}
              {fieldData.required && <span style={{ color: theme.palette.error.main }}> *</span>}
            </Typography>
            {fieldData.options.map((option, index) => (
              <FormControlLabel
                key={index}
                control={<Switch disabled />}
                label={option}
                sx={{ display: 'block', mb: 1 }}
              />
            ))}
          </Box>
        );
      case 'radio':
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {fieldData.label || "Opções Múltiplas"}
              {fieldData.required && <span style={{ color: theme.palette.error.main }}> *</span>}
            </Typography>
            {fieldData.options.map((option, index) => (
              <FormControlLabel
                key={index}
                control={<input type="radio" disabled />}
                label={option}
                sx={{ display: 'block', mb: 1 }}
              />
            ))}
          </Box>
        );
      case 'date':
        return (
          <TextField
            fullWidth
            type="date"
            label={fieldData.label || "Data"}
            placeholder={fieldData.placeholder}
            disabled
            required={fieldData.required}
            margin="normal"
            variant="outlined"
            InputLabelProps={{
              shrink: true,
            }}
            InputProps={{
              style: {
                borderColor: theme.palette.primary.main
              }
            }}
          />
        );
      case 'address':
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {fieldData.label || "Endereço"}
              {fieldData.required && <span style={{ color: theme.palette.error.main }}> *</span>}
            </Typography>
            <Grid container spacing={2}>
              {fieldData.addressConfig.fields.zipCode && (
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="CEP" disabled variant="outlined" size="small" />
                </Grid>
              )}
              {fieldData.addressConfig.fields.street && (
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Rua" disabled variant="outlined" size="small" />
                </Grid>
              )}
              {fieldData.addressConfig.fields.number && (
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Número" disabled variant="outlined" size="small" />
                </Grid>
              )}
              {fieldData.addressConfig.fields.complement && (
                <Grid item xs={12} md={3}>
                  <TextField fullWidth label="Complemento" disabled variant="outlined" size="small" />
                </Grid>
              )}
              {fieldData.addressConfig.fields.neighborhood && (
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="Bairro" disabled variant="outlined" size="small" />
                </Grid>
              )}
              {fieldData.addressConfig.fields.city && (
                <Grid item xs={12} md={4}>
                  <TextField fullWidth label="Cidade" disabled variant="outlined" size="small" />
                </Grid>
              )}
              {fieldData.addressConfig.fields.state && (
                <Grid item xs={12} md={2}>
                  <TextField fullWidth label="UF" disabled variant="outlined" size="small" />
                </Grid>
              )}
              {fieldData.addressConfig.fields.country && (
                <Grid item xs={12} md={6}>
                  <TextField fullWidth label="País" disabled variant="outlined" size="small" />
                </Grid>
              )}
            </Grid>
          </Box>
        );
      case 'hidden':
        return (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom color="textSecondary">
              🔒 Campo Oculto: {fieldData.label || "Campo Oculto"}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Valor: {fieldData.hiddenConfig.value || 'Não definido'}
              {fieldData.hiddenConfig.paramName && ` | Parâmetro URL: ${fieldData.hiddenConfig.paramName}`}
            </Typography>
          </Box>
        );
      case 'file':
        return (
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              {fieldData.label || "Upload de Arquivo"}
              {fieldData.required && <span style={{ color: theme.palette.error.main }}> *</span>}
            </Typography>
            <Box sx={{ 
              border: `2px dashed ${theme.palette.divider}`, 
              borderRadius: 2, 
              p: 3, 
              textAlign: 'center',
              bgcolor: 'grey.50'
            }}>
              <CloudUpload sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="body2" color="textSecondary">
                Clique para fazer upload ou arraste arquivos aqui
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 1 }}>
                Máx: {(fieldData.fileConfig.maxSize / (1024 * 1024)).toFixed(1)}MB
                {fieldData.fileConfig.multiple ? ' | Múltiplos arquivos' : ' | Um arquivo'}
              </Typography>
            </Box>
          </Box>
        );
      default:
        return (
          <TextField
            fullWidth
            label={fieldData.label || "Campo"}
            placeholder={fieldData.placeholder}
            disabled
            required={fieldData.required}
            margin="normal"
            variant="outlined"
            InputProps={{
              style: {
                borderColor: theme.palette.primary.main
              }
            }}
          />
        );
    }
  };
  
  return (
    <BaseModal
      open={open}
      onClose={onClose}
      title={field ? 'Editar Campo' : 'Adicionar Novo Campo'}
      actions={modalActions}
    >
      <Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth variant="outlined" margin="normal">
              <InputLabel>Tipo de Campo</InputLabel>
              <Select
                name="type"
                value={fieldData.type}
                onChange={handleChange}
                label="Tipo de Campo"
              >
                {FieldTypes.map(type => (
                  <MenuItem key={type.value} value={type.value}>
                    <Box display="flex" alignItems="center">
                      <Typography variant="body1" sx={{ mr: 1, fontSize: '1.2rem' }}>
                        {type.icon}
                      </Typography>
                      {type.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Rótulo do Campo"
              name="label"
              value={fieldData.label}
              onChange={handleChange}
              variant="outlined"
              margin="normal"
              error={!!errors.label}
              helperText={errors.label || 'Texto que aparecerá acima do campo'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LabelIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              required
            />
          </Grid>

          {/* NOVO CAMPO - Nome do campo para processamento */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Nome do Campo (ID)"
              name="name"
              value={fieldData.name}
              onChange={handleNameChange}
              variant="outlined"
              margin="normal"
              error={!!errors.name}
              helperText={errors.name || 'Identificador único usado para processar os dados do formulário'}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <KeyIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              required
            />
            <Alert severity="info" sx={{ mt: 1 }}>
              O nome do campo deve ser único e será usado para identificar os dados no processamento do formulário.
              Use apenas letras, números e underscore (_).
            </Alert>
          </Grid>
          
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Placeholder"
              name="placeholder"
              value={fieldData.placeholder}
              onChange={handleChange}
              variant="outlined"
              margin="normal"
              helperText="Texto de exemplo dentro do campo (opcional)"
            />
          </Grid>
          
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={fieldData.required}
                  onChange={handleRequiredChange}
                  color="primary"
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Campo obrigatório</Typography>
                  <Typography variant="caption" color="textSecondary">
                    Se ativado, o usuário precisará preencher este campo
                  </Typography>
                </Box>
              }
            />
          </Grid>
          
          {/* Opções para campos select, checkbox e radio */}
          {['select', 'checkbox', 'radio'].includes(fieldData.type) && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              
              <Box display="flex" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={500}>
                  Opções
                </Typography>
                <Badge
                  badgeContent={fieldData.options.length}
                  color="primary"
                  sx={{ ml: 1 }}
                />
                <Tooltip title="Adicione as opções que o usuário poderá escolher">
                  <IconButton size="small" sx={{ ml: 1 }}>
                    <HelpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
              
              <Box display="flex" alignItems="center" mb={2}>
                <TextField
                  fullWidth
                  label="Nova opção"
                  value={newOption}
                  onChange={(e) => setNewOption(e.target.value)}
                  variant="outlined"
                  onKeyPress={handleKeyPress}
                  error={!!errors.options}
                  helperText={errors.options}
                  placeholder="Digite e pressione Enter para adicionar"
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleAddOption}
                  disabled={!newOption.trim()}
                  startIcon={<AddIcon />}
                  sx={{ ml: 1, height: '56px' }}
                >
                  Adicionar
                </Button>
              </Box>
              
              <Box>
                {fieldData.options.length > 0 ? (
                  <Box sx={{ 
                    display: 'flex', 
                    flexWrap: 'wrap', 
                    gap: 1, 
                    p: 2, 
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    bgcolor: 'background.default'
                  }}>
                    {fieldData.options.map((option, index) => (
                      <Chip
                        key={index}
                        label={option}
                        onDelete={() => handleRemoveOption(index)}
                        color="primary"
                        variant="outlined"
                        sx={{ m: 0.5 }}
                      />
                    ))}
                  </Box>
                ) : (
                  <Alert 
                    severity="info" 
                    variant="outlined"
                    sx={{ borderRadius: 1 }}
                  >
                    <Typography variant="body2">
                      Nenhuma opção adicionada. Adicione pelo menos uma opção.
                    </Typography>
                  </Alert>
                )}
              </Box>
            </Grid>
          )}
          
          {/* Validação personalizada para campos específicos */}
          {['text', 'email', 'phone'].includes(fieldData.type) && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Validação personalizada (opcional)"
                name="validation"
                value={fieldData.validation}
                onChange={handleChange}
                variant="outlined"
                margin="normal"
                helperText={
                  fieldData.type === 'email' 
                    ? 'Padrão de e-mail será aplicado automaticamente' 
                    : fieldData.type === 'phone' 
                      ? 'Padrão de telefone será aplicado automaticamente'
                      : 'Expressão regular ou regra de validação (opcional)'
                }
                disabled={['email', 'phone'].includes(fieldData.type)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CodeIcon color="action" />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          )}
          
          {/* Configurações específicas para campo de endereço */}
          {fieldData.type === 'address' && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={500}>
                  Configurações de Endereço
                </Typography>
                <AddressIcon sx={{ ml: 1, color: 'primary.main' }} />
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fieldData.addressConfig.enableAutocomplete}
                        onChange={(e) => handleAddressConfigChange('enableAutocomplete', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Preenchimento automático via CEP"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fieldData.addressConfig.enableGeolocation}
                        onChange={(e) => handleAddressConfigChange('enableGeolocation', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Capturar coordenadas geográficas"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                    Campos do Endereço
                  </Typography>
                  <Grid container spacing={1}>
                    {Object.entries(fieldData.addressConfig.fields).map(([field, enabled]) => (
                      <Grid item xs={12} sm={6} md={4} key={field}>
                        <FormControlLabel
                          control={
                            <Switch
                              size="small"
                              checked={enabled}
                              onChange={(e) => handleAddressFieldChange(field, e.target.checked)}
                              color="primary"
                            />
                          }
                          label={{
                            zipCode: 'CEP',
                            street: 'Rua',
                            number: 'Número',
                            complement: 'Complemento',
                            neighborhood: 'Bairro',
                            city: 'Cidade',
                            state: 'Estado',
                            country: 'País'
                          }[field]}
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: '0.875rem' } }}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          )}
          
          {/* Configurações específicas para campo oculto */}
          {fieldData.type === 'hidden' && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={500}>
                  Configurações de Campo Oculto
                </Typography>
                <VisibilityOff sx={{ ml: 1, color: 'primary.main' }} />
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Valor Padrão"
                    value={fieldData.hiddenConfig.value}
                    onChange={(e) => handleHiddenConfigChange('value', e.target.value)}
                    variant="outlined"
                    helperText="Valor que será enviado no formulário"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Parâmetro URL (opcional)"
                    value={fieldData.hiddenConfig.paramName}
                    onChange={(e) => handleHiddenConfigChange('paramName', e.target.value)}
                    variant="outlined"
                    helperText="Capturar valor da URL (ex: utm_source)"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fieldData.hiddenConfig.allowOverride}
                        onChange={(e) => handleHiddenConfigChange('allowOverride', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Permitir sobrescrita do valor"
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          
          {/* Configurações específicas para upload de arquivo */}
          {fieldData.type === 'file' && (
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box display="flex" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight={500}>
                  Configurações de Upload
                </Typography>
                <CloudUpload sx={{ ml: 1, color: 'primary.main' }} />
              </Box>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Tamanho Máximo (MB)"
                    value={Math.round(fieldData.fileConfig.maxSize / (1024 * 1024))}
                    onChange={(e) => handleFileConfigChange('maxSize', parseInt(e.target.value) * 1024 * 1024)}
                    variant="outlined"
                    InputProps={{
                      inputProps: { min: 1, max: 100 }
                    }}
                    helperText="Tamanho máximo por arquivo"
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Pasta de Destino"
                    value={fieldData.fileConfig.folder}
                    onChange={(e) => handleFileConfigChange('folder', e.target.value)}
                    variant="outlined"
                    helperText="Pasta onde os arquivos serão salvos"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FolderIcon fontSize="small" />
                        </InputAdornment>
                      )
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Local de Armazenamento</InputLabel>
                    <Select
                      value={fieldData.fileConfig.storage}
                      onChange={(e) => handleFileConfigChange('storage', e.target.value)}
                      label="Local de Armazenamento"
                    >
                      <MenuItem value="local">Servidor Local</MenuItem>
                      <MenuItem value="cloud">Nuvem</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={fieldData.fileConfig.multiple}
                        onChange={(e) => handleFileConfigChange('multiple', e.target.checked)}
                        color="primary"
                      />
                    }
                    label="Múltiplos arquivos"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tipos de Arquivo Permitidos
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {[
                      { value: 'image/jpeg', label: 'JPEG' },
                      { value: 'image/png', label: 'PNG' },
                      { value: 'image/gif', label: 'GIF' },
                      { value: 'application/pdf', label: 'PDF' },
                      { value: 'application/msword', label: 'DOC' },
                      { value: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', label: 'DOCX' },
                      { value: 'application/vnd.ms-excel', label: 'XLS' },
                      { value: 'text/plain', label: 'TXT' }
                    ].map((type) => {
                      const isSelected = fieldData.fileConfig.allowedTypes.includes(type.value);
                      return (
                        <Chip
                          key={type.value}
                          label={type.label}
                          clickable
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          onClick={() => {
                            const newTypes = isSelected 
                              ? fieldData.fileConfig.allowedTypes.filter(t => t !== type.value)
                              : [...fieldData.fileConfig.allowedTypes, type.value];
                            handleAllowedTypesChange(newTypes);
                          }}
                        />
                      );
                    })}
                  </Box>
                </Grid>
                
                {/* Configurações de PDF Parsing */}
                {fieldData.fileConfig.allowedTypes.includes('application/pdf') && (
                  <>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                        <CloudUpload sx={{ mr: 1, color: 'primary.main' }} />
                        Configurações de Processamento de PDF
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={fieldData.fileConfig.enablePdfParsing || false}
                            onChange={(e) => handleFileConfigChange('enablePdfParsing', e.target.checked)}
                            color="primary"
                          />
                        }
                        label="Habilitar processamento automático de PDF"
                      />
                      <Typography variant="caption" display="block" color="textSecondary">
                        Extrair dados do PDF e salvar como campos personalizados do contato
                      </Typography>
                    </Grid>
                    
                    {fieldData.fileConfig.enablePdfParsing && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth
                            label="Prefixo dos Campos"
                            value={fieldData.fileConfig.pdfParsingConfig?.fieldPrefix || 'pdf_'}
                            onChange={(e) => {
                              const newConfig = {
                                ...fieldData.fileConfig,
                                pdfParsingConfig: {
                                  ...fieldData.fileConfig.pdfParsingConfig,
                                  fieldPrefix: e.target.value
                                }
                              };
                              setFieldData(prev => ({
                                ...prev,
                                fileConfig: newConfig
                              }));
                            }}
                            variant="outlined"
                            helperText="Prefixo para os campos extraídos (ex: pdf_nome)"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              Dados a Extrair
                            </Typography>
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={fieldData.fileConfig.pdfParsingConfig?.extractText !== false}
                                  onChange={(e) => {
                                    const newConfig = {
                                      ...fieldData.fileConfig,
                                      pdfParsingConfig: {
                                        ...fieldData.fileConfig.pdfParsingConfig,
                                        extractText: e.target.checked
                                      }
                                    };
                                    setFieldData(prev => ({
                                      ...prev,
                                      fileConfig: newConfig
                                    }));
                                  }}
                                  color="primary"
                                />
                              }
                              label="Texto completo"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={fieldData.fileConfig.pdfParsingConfig?.extractMetadata !== false}
                                  onChange={(e) => {
                                    const newConfig = {
                                      ...fieldData.fileConfig,
                                      pdfParsingConfig: {
                                        ...fieldData.fileConfig.pdfParsingConfig,
                                        extractMetadata: e.target.checked
                                      }
                                    };
                                    setFieldData(prev => ({
                                      ...prev,
                                      fileConfig: newConfig
                                    }));
                                  }}
                                  color="primary"
                                />
                              }
                              label="Metadados"
                            />
                            <FormControlLabel
                              control={
                                <Switch
                                  size="small"
                                  checked={fieldData.fileConfig.pdfParsingConfig?.saveAsCustomFields !== false}
                                  onChange={(e) => {
                                    const newConfig = {
                                      ...fieldData.fileConfig,
                                      pdfParsingConfig: {
                                        ...fieldData.fileConfig.pdfParsingConfig,
                                        saveAsCustomFields: e.target.checked
                                      }
                                    };
                                    setFieldData(prev => ({
                                      ...prev,
                                      fileConfig: newConfig
                                    }));
                                  }}
                                  color="primary"
                                />
                              }
                              label="Salvar no contato"
                            />
                          </Box>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                            <Typography variant="body2">
                              <strong>Como funciona:</strong> Quando um PDF for enviado neste campo, o sistema irá extrair automaticamente 
                              os dados e salvá-los como campos personalizados no contato. Você poderá visualizar esses dados 
                              na página de detalhes do contato.
                            </Typography>
                          </Alert>
                        </Grid>
                      </>
                    )}
                  </>
                )}
              </Grid>
            </Grid>
          )}
          
          {/* Visualização do campo */}
          <Grid item xs={12}>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle1" gutterBottom>
              Pré-visualização
            </Typography>
            <Box 
              sx={{ 
                p: 2, 
                border: `1px solid ${theme.palette.divider}`, 
                borderRadius: 2,
                bgcolor: 'background.paper'
              }}
            >
              {renderFieldPreview()}
            </Box>
          </Grid>
        </Grid>
      </Box>
    </BaseModal>
  );
};

export default FieldEditorDialog;